import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, X, SlidersHorizontal, Bike, Car, Phone, MessageCircle, Sparkles, AlertTriangle, Calendar } from "lucide-react";
import { BRAND_PHONE, BRAND_WHATSAPP } from "@/lib/brand";
import CatalogDateGate from "@/components/catalog/CatalogDateGate";
import ScooterCard from "@/components/catalog/ScooterCard";
import UnavailableSection from "@/components/catalog/UnavailableSection";

const DEFAULT_DISP = [0, 1500];
const DEFAULT_RATE = [0, 200];

// localStorage key for date pre-fill (Iter 33). Stored payload:
// { startDate: "YYYY-MM-DD", endDate: "YYYY-MM-DD", savedAt: <unix ms> }
// We expire after 24h to avoid serving stale dates next season.
const DATES_LS_KEY = "rentlupi_catalog_dates";
const DATES_LS_TTL_MS = 24 * 60 * 60 * 1000;

// 48-hour lead-time (Iter 19): we cannot book < 48h in advance.
function dateNDaysFromNow(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
// Combine YYYY-MM-DD with a default local time → ISO string (UTC).
// Pickup at 10:00 local, return at 18:00 local — these are the standard slots
// used in ScooterDetail (the user refines them at checkout).
function toIso(dateStr, hour) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T${String(hour).padStart(2, "0")}:00:00`);
  return d.toISOString();
}

// Restore the previously-picked dates if they are still valid:
//  - saved within the last 24h (avoid old / "next season" picks)
//  - pickup still >= MIN_PICKUP (48h from now)
//  - end > start
// Returns { startDate, endDate } or null. Pure (no React deps) so it can run
// inline as the useState initializer.
function loadStoredDates(minPickup) {
  try {
    const raw = window.localStorage.getItem(DATES_LS_KEY);
    if (!raw) return null;
    const { startDate, endDate, savedAt } = JSON.parse(raw);
    if (!startDate || !endDate || !savedAt) return null;
    if (Date.now() - savedAt > DATES_LS_TTL_MS) return null;
    if (startDate < minPickup) return null;
    if (endDate <= startDate) return null;
    return { startDate, endDate };
  } catch {
    return null;
  }
}

export default function Catalog() {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === "en";

  // Iter 50/62 — SEO: title + description tuned for "noleggio scooter Elba",
  // "noleggio auto Elba", "rent scooter Elba", "autonoleggio Marciana Marina".
  // Description stays ≤155 chars so Google shows it in full.
  useSeo({
    title: isEN
      ? "Rent Scooter & Car — Elba Island · Marciana Marina · Rent Lupi"
      : "Noleggio Scooter e Auto Isola d'Elba · Marciana Marina · Rent Lupi",
    description: isEN
      ? "Rent scooter or car on Elba Island — Marciana Marina. Family-run since 1982, in-house workshop, digital contract, instant online booking."
      : "Noleggio scooter e autonoleggio all'Isola d'Elba a Marciana Marina dal 1982. Prenota online, officina interna, contratto digitale, caparra Revolut.",
    canonical: "https://rentlupielba.it/catalog",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "url": "https://rentlupielba.it/catalog",
      "name": isEN
        ? "Scooter & Car Catalogue — Rent Lupi Elba"
        : "Catalogo Scooter e Auto — Rent Lupi Elba",
      "description": isEN
        ? "Full fleet of scooters and cars available for rent on Elba Island."
        : "Flotta completa di scooter e auto disponibili per il noleggio all'Isola d'Elba.",
      "isPartOf": { "@type": "WebSite", "url": "https://rentlupielba.it/", "name": "Rent Lupi" },
      "provider": { "@type": "AutoRental", "@id": "https://rentlupielba.it/#business" },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://rentlupielba.it/" },
          { "@type": "ListItem", "position": 2, "name": isEN ? "Catalogue" : "Catalogo", "item": "https://rentlupielba.it/catalog" },
        ],
      },
    },
  });

  const MIN_PICKUP = dateNDaysFromNow(2);
  const [vehicleType, setVehicleType] = useState("scooter");
  const [scooters, setScooters] = useState(null);
  const [search, setSearch] = useState("");
  const [disp, setDisp] = useState(DEFAULT_DISP);
  const [rate, setRate] = useState(DEFAULT_RATE);
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState({ search, disp, rate });

  // Date-first flow (Iter 31): customer MUST pick dates before seeing the catalog.
  // Iter 33: restore the last picked dates (within 24h) so returning customers
  // don't re-type. Validation still runs on every render — if the saved dates
  // are now < 48h away, loadStoredDates() returns null.
  const [startDate, setStartDate] = useState(() => loadStoredDates(MIN_PICKUP)?.startDate || "");
  const [endDate, setEndDate] = useState(() => loadStoredDates(MIN_PICKUP)?.endDate || "");
  const [showUnavail, setShowUnavail] = useState(false);

  // Persist the date pair whenever BOTH are filled. We don't need the
  // dates to be valid (the load-side already filters expired/invalid);
  // storing partial selections would just yield a no-op load.
  useEffect(() => {
    if (!startDate || !endDate) return;
    try {
      window.localStorage.setItem(
        DATES_LS_KEY,
        JSON.stringify({ startDate, endDate, savedAt: Date.now() }),
      );
    } catch {
      /* localStorage disabled (private mode / quota) — silently ignore. */
    }
  }, [startDate, endDate]);

  // Derived: human-readable error for the date inputs (null when OK).
  // Hard-blocks Sunday pickup (we're closed for hand-out on Sundays).
  // Soft-warns Saturday pickup (by appointment — proceed but inform).
  const dateError = useMemo(() => {
    if (!startDate || !endDate) return "";
    if (startDate < MIN_PICKUP) {
      return isEN
        ? "Pickup must be at least 48h from now — please contact us via WhatsApp for last-minute bookings."
        : "Il ritiro deve essere almeno 48h da ora — per prenotazioni dell'ultimo minuto contattaci su WhatsApp.";
    }
    if (endDate <= startDate) {
      return isEN
        ? "Return date must be after pickup date."
        : "La data di riconsegna deve essere successiva al ritiro.";
    }
    // Day-of-week check on pickup. new Date("YYYY-MM-DD") is treated as UTC,
    // but since we only care about the calendar day (not local time), this is fine.
    const pickupDow = new Date(startDate + "T12:00:00").getDay(); // 0=Sun, 6=Sat
    if (pickupDow === 0) {
      return isEN
        ? "We're closed for pickups on Sundays. Please choose another day."
        : "La Domenica siamo chiusi al ritiro. Scegli un altro giorno.";
    }
    return "";
  }, [startDate, endDate, MIN_PICKUP, isEN]);

  // Soft-warning (not blocking) — informs the customer that Saturday pickup
  // requires a phone call/WhatsApp to confirm the slot.
  const dateWarning = useMemo(() => {
    if (!startDate || dateError) return "";
    const pickupDow = new Date(startDate + "T12:00:00").getDay();
    if (pickupDow === 6) {
      return isEN
        ? "Saturday pickup is by appointment — we'll confirm the exact time via WhatsApp after you book."
        : "Il Sabato il ritiro è su appuntamento — ti confermeremo l'orario su WhatsApp dopo la prenotazione.";
    }
    return "";
  }, [startDate, dateError, isEN]);

  // Derived: dates are usable for an availability query.
  // Sunday pickup blocks the catalog entirely; Saturday pickup is allowed.
  const datesValid = useMemo(() => {
    if (!startDate || !endDate) return false;
    if (startDate < MIN_PICKUP) return false;
    if (endDate <= startDate) return false;
    const pickupDow = new Date(startDate + "T12:00:00").getDay();
    if (pickupDow === 0) return false; // Sunday — closed
    return true;
  }, [startDate, endDate, MIN_PICKUP]);

  useEffect(() => {
    const tm = setTimeout(() => setDebounced({ search, disp, rate }), 300);
    return () => clearTimeout(tm);
  }, [search, disp, rate]);

  useEffect(() => {
    if (!datesValid) return;
    let active = true;
    // "last_minute" pseudo-tab → fetch ALL vehicles then keep only those flagged.
    const isLM = vehicleType === "last_minute";
    const params = isLM ? {} : { vehicle_type: vehicleType };
    if (debounced.search.trim()) params.search = debounced.search.trim();
    if (debounced.disp[0] > 0) params.min_displacement = debounced.disp[0];
    if (debounced.disp[1] < DEFAULT_DISP[1]) params.max_displacement = debounced.disp[1];
    if (debounced.rate[0] > 0) params.min_rate = debounced.rate[0];
    if (debounced.rate[1] < DEFAULT_RATE[1]) params.max_rate = debounced.rate[1];
    // Forward date range to backend so it computes `is_available_in_range`.
    params.start_at = toIso(startDate, 10);
    params.end_at = toIso(endDate, 18);
    api.get("/scooters", { params })
      .then((r) => {
        if (!active) return;
        let list = r.data;
        if (isLM) {
          const today = new Date().toISOString().slice(0, 10);
          list = list.filter((s) => s.last_minute_active && (!s.last_minute_until || s.last_minute_until >= today));
        }
        setScooters(list);
      })
      .catch(() => { if (active) setScooters([]); });
    return () => { active = false; };
  }, [debounced, vehicleType, datesValid, startDate, endDate]);

  const activeFilters = useMemo(() => {
    let n = 0;
    if (search.trim()) n++;
    if (disp[0] > 0 || disp[1] < DEFAULT_DISP[1]) n++;
    if (rate[0] > 0 || rate[1] < DEFAULT_RATE[1]) n++;
    return n;
  }, [search, disp, rate]);

  const reset = () => { setSearch(""); setDisp(DEFAULT_DISP); setRate(DEFAULT_RATE); };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-2">{t("catalog.tag")}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
          {i18n.language === "en" ? "Vehicle Catalog" : "Catalogo Veicoli"}
        </h1>
        <p className="text-zinc-400 mt-2">{t("catalog.subtitle")}</p>
      </div>

      {/* ───── DATE-FIRST GATE (Iter 31) ───── */}
      <CatalogDateGate
        startDate={startDate}
        endDate={endDate}
        onStartChange={setStartDate}
        onEndChange={setEndDate}
        minPickup={MIN_PICKUP}
        dateError={dateError}
        dateWarning={dateWarning}
        datesValid={datesValid}
        isEN={isEN}
      />

      {!datesValid && (
        <div className="border border-zinc-800 rounded-2xl p-10 text-center text-zinc-500" data-testid="catalog-locked">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">
            {isEN
              ? "Catalogue will appear once you pick valid dates above."
              : "Il catalogo apparirà dopo aver scelto date valide qui sopra."}
          </p>
        </div>
      )}

      {datesValid && (<>

      <Tabs value={vehicleType} onValueChange={setVehicleType}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="scooter" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black gap-2" data-testid="tab-scooter">
            <Bike className="w-4 h-4" /> Scooter
          </TabsTrigger>
          <TabsTrigger value="auto" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black gap-2" data-testid="tab-auto">
            <Car className="w-4 h-4" /> {i18n.language === "en" ? "Cars" : "Auto"}
          </TabsTrigger>
          <TabsTrigger value="last_minute" className="data-[state=active]:bg-red-500 data-[state=active]:text-white gap-2" data-testid="tab-last-minute">
            🔥 Last Minute
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Age & licence requirements — REQUIRED disclosure (D.Lgs. 206/2005 art.49).
          Includes explicit refusal clauses for minors and learner's permits ("foglio rosa"). */}
      {vehicleType === "auto" ? (
        <div className="rounded-xl border-2 border-[#FFD700]/70 bg-gradient-to-br from-[#FFD700]/[0.10] via-[#FFD700]/[0.05] to-transparent p-4 flex items-start gap-3" data-testid="age-banner-auto">
          <Car className="w-5 h-5 text-[#FFD700] shrink-0 mt-0.5" />
          <div className="text-sm text-zinc-100 leading-relaxed flex-1">
            <div className="font-semibold text-[#FFD700] mb-1">
              {i18n.language === "en" ? "Car rental requirements" : "Requisiti per il noleggio auto"}
            </div>
            <p>
              {i18n.language === "en"
                ? "Minimum age 21 — valid category B driving licence held for at least 1 year — original ID + licence required at pickup."
                : "Età minima 21 anni compiuti — patente B in corso di validità rilasciata da almeno 1 anno — documento d'identità e patente in originale obbligatori al ritiro."}
            </p>
            <div className="mt-2 pt-2 border-t border-[#FFD700]/30 flex items-start gap-2 text-[12.5px]">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-200">
                <span className="font-semibold">{i18n.language === "en" ? "Rental NOT allowed: " : "Noleggio NON consentito: "}</span>
                {i18n.language === "en"
                  ? "drivers under 18, learner's permit (\"foglio rosa\") only — only a valid driving licence is accepted."
                  : "minorenni; con solo foglio rosa — è richiesta patente valida, nessuna eccezione."}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border-2 border-[#FFD700]/40 bg-[#FFD700]/[0.04] p-3 flex items-start gap-3" data-testid="age-banner-scooter">
          <Bike className="w-4 h-4 text-[#FFD700] shrink-0 mt-0.5" />
          <div className="text-[12.5px] text-zinc-200 leading-snug flex-1">
            <p>
              {i18n.language === "en"
                ? "Minimum age 18 · valid licence (A1/A2/A or B depending on engine size) required at pickup."
                : "Età minima 18 anni · patente valida (A1/A2/A o B in base alla cilindrata) richiesta al ritiro."}
            </p>
            <div className="mt-1.5 pt-1.5 border-t border-[#FFD700]/20 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-200 text-[12px]">
                <span className="font-semibold">{i18n.language === "en" ? "NOT allowed: " : "NON consentito: "}</span>
                {i18n.language === "en"
                  ? "under 18, learner's permit (\"foglio rosa\") only — a valid driving licence is mandatory."
                  : "minorenni; con solo foglio rosa — è richiesta patente valida."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ───── Auto-discount tiers ───── */}
      <div
        className="rounded-2xl border border-[#FFD700]/30 bg-gradient-to-br from-[#FFD700]/[0.08] via-zinc-900/40 to-transparent p-4 sm:p-5"
        data-testid="discount-tiers-banner"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#FFD700]/20 border border-[#FFD700]/40 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-[#FFD700]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFD700] mb-1">
              {i18n.language === "en" ? "Long-stay savings" : "Più giorni, più risparmi"}
            </p>
            <p className="font-display text-base sm:text-lg font-bold leading-tight text-zinc-100">
              {i18n.language === "en"
                ? "Automatic discount applied at checkout — no code needed."
                : "Sconto automatico al checkout — nessun codice da inserire."}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {i18n.language === "en" ? "7+ days" : "7+ giorni"}
                </div>
                <div className="font-display text-2xl font-bold text-[#FFD700]">−5%</div>
              </div>
              <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {i18n.language === "en" ? "10+ days" : "10+ giorni"}
                </div>
                <div className="font-display text-2xl font-bold text-[#FFD700]">
                  {i18n.language === "en" ? "−7.5%" : "−7,5%"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───── Custom-quote CTA for very long rentals ───── */}
      <div
        className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        data-testid="custom-quote-banner"
      >
        <div className="min-w-0">
          <p className="font-display text-base sm:text-lg font-bold leading-tight text-zinc-100">
            {i18n.language === "en"
              ? "Renting for more than 10 days?"
              : "Noleggio superiore ai 10 giorni?"}
          </p>
          <p className="text-sm text-zinc-400 mt-0.5 leading-snug">
            {i18n.language === "en"
              ? "Contact us for a tailored quote — we'll build the best deal for your stay."
              : "Contattaci per ricevere il tuo preventivo su misura — ti faremo l'offerta migliore."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={`tel:${BRAND_PHONE}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-950 border border-zinc-800 hover:border-[#FFD700]/60 hover:text-[#FFD700] text-zinc-200 text-sm font-medium transition-colors"
            data-testid="custom-quote-phone"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">{i18n.language === "en" ? "Call" : "Chiama"}</span>
          </a>
          <a
            href={`https://wa.me/${BRAND_WHATSAPP.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
              i18n.language === "en"
                ? "Hi! I'd like a custom quote for a long rental at Rent Lupi."
                : "Ciao! Vorrei un preventivo su misura per un noleggio lungo a Rent Lupi."
            )}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 hover:bg-[#25D366]/25 text-[#25D366] text-sm font-semibold transition-colors"
            data-testid="custom-quote-whatsapp"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder={t("catalog.search_placeholder")}
                   className="bg-zinc-900 border-zinc-800 focus:ring-[#FFD700]/50 pl-9"
                   data-testid="catalog-search-input" />
          </div>
          <Button type="button" variant="outline" onClick={() => setOpen((o) => !o)}
                  className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 text-zinc-200 relative"
                  data-testid="catalog-toggle-filters">
            <SlidersHorizontal className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("catalog.filters")}</span>
            {activeFilters > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FFD700] text-black text-[10px] font-bold w-5 h-5 rounded-full grid place-items-center">{activeFilters}</span>
            )}
          </Button>
          {activeFilters > 0 && (
            <Button type="button" variant="ghost" size="sm" onClick={reset}
                    className="text-zinc-400 hover:text-[#FFD700]" data-testid="catalog-reset-filters">
              <X className="w-4 h-4 mr-1" /> {t("common.reset")}
            </Button>
          )}
        </div>

        {open && (
          <div className="border border-zinc-800 bg-zinc-900/50 rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-zinc-300">{t("catalog.displacement")}</label>
                <span className="text-xs text-zinc-500 font-mono">{disp[0]} – {disp[1]} cc</span>
              </div>
              <Slider min={0} max={DEFAULT_DISP[1]} step={25} value={disp} onValueChange={setDisp} data-testid="catalog-filter-displacement" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-zinc-300">{t("catalog.rate")}</label>
                <span className="text-xs text-zinc-500 font-mono">€{rate[0]} – €{rate[1]}</span>
              </div>
              <Slider min={0} max={DEFAULT_RATE[1]} step={1} value={rate} onValueChange={setRate} data-testid="catalog-filter-rate" />
            </div>
          </div>
        )}
      </div>

      {scooters === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-72 bg-zinc-900 rounded-xl" />)}
        </div>
      ) : scooters.length === 0 ? (
        <div className="border border-zinc-800 rounded-xl p-10 text-center text-zinc-400">
          {activeFilters > 0 ? t("catalog.empty_with_filters") : t("catalog.empty_no_scooters")}
        </div>
      ) : (() => {
        // Iter 31 — partition by availability for the picked date range.
        // Iter 34 — maintenance vehicles are now shown next to the available
        // ones (same top grid) as non-clickable cards with phone/WhatsApp CTA.
        // Iter 54 — winter-storage vehicles also go in the top grid as
        // clickable "prebook next season" cards. Only vehicles busy with an
        // overlapping booking are collapsed into "Non disponibili".
        const available    = scooters.filter((s) => s.is_available_in_range !== false && s.status !== "manutenzione" && s.status !== "sospeso");
        const winterStock  = scooters.filter((s) => s.status === "sospeso" && s.is_available_in_range === false);
        const winterOk     = scooters.filter((s) => s.status === "sospeso" && s.is_available_in_range !== false);
        const mustCall     = scooters.filter((s) => s.status === "manutenzione");
        const unavailable  = scooters.filter((s) => s.is_available_in_range === false && s.status !== "manutenzione" && s.status !== "sospeso");
        const datesQs = `?start_at=${encodeURIComponent(startDate)}&end_at=${encodeURIComponent(endDate)}`;
        const topGridCount = available.length + mustCall.length + winterStock.length + winterOk.length;

        return (
          <div className="space-y-8">
            {/* Disponibili + manutenzione (stesso grid in cima) */}
            {topGridCount > 0 ? (
              <div data-testid="catalog-available-section">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-display text-2xl font-bold tracking-tight">
                    {isEN
                      ? `Available on your dates · ${available.length}${mustCall.length ? ` (+${mustCall.length} by phone)` : ""}${(winterStock.length + winterOk.length) ? ` (+${winterStock.length + winterOk.length} next season)` : ""}`
                      : `Disponibili per le tue date · ${available.length}${mustCall.length ? ` (+${mustCall.length} su richiesta)` : ""}${(winterStock.length + winterOk.length) ? ` (+${winterStock.length + winterOk.length} prossima stagione)` : ""}`}
                  </h2>
                  <span className="text-xs text-zinc-500">
                    {startDate.split("-").reverse().join("/")} → {endDate.split("-").reverse().join("/")}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="catalog-grid">
                  {available.map((s) => (
                    <ScooterCard key={s.id} s={s} isUnavail={false} linkQuery={datesQs} inDateFlow={true} isEN={isEN} t={t} />
                  ))}
                  {winterOk.map((s) => (
                    <ScooterCard key={s.id} s={s} isUnavail={false} linkQuery={datesQs} inDateFlow={true} isEN={isEN} t={t} />
                  ))}
                  {winterStock.map((s) => (
                    <ScooterCard key={s.id} s={s} isUnavail={true} inDateFlow={true} isEN={isEN} t={t} />
                  ))}
                  {mustCall.map((s) => (
                    <ScooterCard key={s.id} s={s} isUnavail={true} inDateFlow={true} isEN={isEN} t={t} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="border border-amber-500/30 bg-amber-500/5 rounded-2xl p-8 text-center" data-testid="catalog-no-available">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                <p className="font-display text-lg font-semibold text-amber-200">
                  {isEN
                    ? "No vehicles available on the selected dates."
                    : "Nessun veicolo disponibile nelle date selezionate."}
                </p>
                <p className="text-sm text-zinc-400 mt-1">
                  {isEN
                    ? "Try different dates or contact us on WhatsApp — we may still have something for you."
                    : "Prova date diverse o contattaci su WhatsApp — potremmo comunque avere qualcosa per te."}
                </p>
                <a
                  href={`https://wa.me/${BRAND_WHATSAPP.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
                    isEN
                      ? `Hi! I'd like to rent from ${startDate} to ${endDate} at Rent Lupi.`
                      : `Ciao! Vorrei noleggiare dal ${startDate} al ${endDate} da Rent Lupi.`
                  )}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 hover:bg-[#25D366]/25 text-[#25D366] text-sm font-semibold transition"
                  data-testid="catalog-no-available-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            )}

            {/* Non disponibili nelle date — collassato (solo veicoli realmente occupati) */}
            <UnavailableSection
              items={unavailable}
              open={showUnavail}
              onToggle={() => setShowUnavail((v) => !v)}
              isEN={isEN}
              t={t}
            />
          </div>
        );
      })()}
      </>)}
    </div>
  );
}
